// Voxel raycast — Amanatides & Woo DDA (ENG-19), ported from the legacy engine.
// O(reach) in the number of voxels crossed, independent of world geometry. M0.3
// tests full cubes only; sub-AABB `raycastShape` boxes come with the shape library
// (M2/M10). `isSolid` decides hits; the entry face normal is reported.
import type { SolidFn } from './physics';

export interface RaycastHit {
  /** The solid voxel that was hit. */
  x: number;
  y: number;
  z: number;
  /** Face normal of the entry face (points back toward the ray origin). */
  nx: number;
  ny: number;
  nz: number;
  /** Distance along the ray to the hit. */
  distance: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** First solid voxel hit within `maxDist`, or null. Direction need not be unit. */
export function raycastVoxel(isSolid: SolidFn, origin: Vec3, dir: Vec3, maxDist: number): RaycastHit | null {
  // Normalize so `t` is a true distance and tDelta math is correct.
  const len = Math.hypot(dir.x, dir.y, dir.z);
  if (len === 0) return null;
  const dx = dir.x / len, dy = dir.y / len, dz = dir.z / len;

  let x = Math.floor(origin.x), y = Math.floor(origin.y), z = Math.floor(origin.z);
  const stepX = Math.sign(dx), stepY = Math.sign(dy), stepZ = Math.sign(dz);
  const tDeltaX = dx !== 0 ? Math.abs(1 / dx) : Infinity;
  const tDeltaY = dy !== 0 ? Math.abs(1 / dy) : Infinity;
  const tDeltaZ = dz !== 0 ? Math.abs(1 / dz) : Infinity;
  let tMaxX = dx !== 0 ? (stepX > 0 ? x + 1 - origin.x : origin.x - x) * tDeltaX : Infinity;
  let tMaxY = dy !== 0 ? (stepY > 0 ? y + 1 - origin.y : origin.y - y) * tDeltaY : Infinity;
  let tMaxZ = dz !== 0 ? (stepZ > 0 ? z + 1 - origin.z : origin.z - z) * tDeltaZ : Infinity;
  let nx = 0, ny = 0, nz = 0, t = 0;

  while (t <= maxDist) {
    if (isSolid(x, y, z)) {
      return { x, y, z, nx, ny, nz, distance: t };
    }
    if (tMaxX < tMaxY && tMaxX < tMaxZ) {
      t = tMaxX; tMaxX += tDeltaX; x += stepX; nx = -stepX; ny = 0; nz = 0;
    } else if (tMaxY < tMaxZ) {
      t = tMaxY; tMaxY += tDeltaY; y += stepY; nx = 0; ny = -stepY; nz = 0;
    } else {
      t = tMaxZ; tMaxZ += tDeltaZ; z += stepZ; nx = 0; ny = 0; nz = -stepZ;
    }
  }
  return null;
}
